import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { FiArrowRight, FiClock, FiUsers, FiShield, FiCheck, FiLoader, FiZap, FiLock } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '../contexts/AuthContext';

/**
 * Growth Plan §2.2 — public Founding Member landing.
 *
 * States:
 *   loading      — fetching active batch
 *   no_batch     — nothing active → shows generic pricing CTA instead
 *   available    — batch is open → shows offer + Claim CTA
 *   signed_out   — not logged in → forces login before claim
 *   claiming     — RPC in flight
 *   claimed      — coupon locked in → auto-continues to checkout
 *   error        — batch full / expired / other
 *
 * On successful claim we drop the user into /account/billing with the
 * Stripe promo code auto-applied via ?founding=<code>. AccountBilling
 * can pass this promo to create-checkout so the discount lands in the
 * hosted Stripe session.
 */

const FoundingMember = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    (async () => {
      try {
        const { data } = await supabase.rpc('get_active_founding_batch');
        if (data?.success) setBatch(data.batch);
      } catch (err) {
        console.error('[founding] failed to load batch', err);
      } finally {
        setLoading(false);
      }
    })();
    // Tick every 1s so the countdown updates live.
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const claim = async () => {
    if (!user) {
      // The button label reads "Sign in to claim", so this route must be
      // /login — not /register. Founding Member is aimed at users we
      // already have on file (Growth Plan §2.2: outreach targets the
      // existing 120 signups), so signup is the wrong first step.
      // Login page respects ?returnTo=… and routes back after auth.
      navigate(`/login?returnTo=${encodeURIComponent('/founding')}`);
      return;
    }
    setClaiming(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('claim_founding_seat', { p_batch_id: batch.id });
      if (rpcErr) throw rpcErr;
      if (!data?.success) {
        const map = {
          SEATS_FULL_OR_EXPIRED: 'This batch is fully claimed. Standard pricing applies.',
          NOT_AUTHENTICATED:     'You need to sign in first.',
          BATCH_NOT_FOUND:       'Offer no longer available.',
        };
        setError(map[data?.error] || data?.error || 'Failed to claim.');
        return;
      }
      // Straight into billing with the promo code, and let AccountBilling
      // pass it to create-checkout as promo_code.
      navigate(`/account/billing?founding=${encodeURIComponent(data.stripe_coupon_id)}&plan=pro_trader`);
    } catch (err) {
      setError(err.message || 'Failed to claim.');
    } finally {
      setClaiming(false);
    }
  };

  const remaining = batch ? batch.seats_remaining : 0;
  const total = batch ? batch.seats_total : 0;
  const pctFilled = total > 0 ? ((total - remaining) / total) * 100 : 0;

  // Countdown to valid_until
  const countdown = (() => {
    if (!batch?.valid_until) return null;
    const target = new Date(batch.valid_until).getTime();
    const ms = target - now;
    if (ms <= 0) return null;
    const d = Math.floor(ms / 86_400_000);
    const h = Math.floor((ms % 86_400_000) / 3_600_000);
    const m = Math.floor((ms % 3_600_000) / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return { d, h, m, s };
  })();

  return (
    <div className="min-h-screen bg-gray-950 relative overflow-hidden">
      {/* Subtle grid backdrop */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:48px_48px]" />
      {/* Cyan/purple glow */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-purple-500/10 blur-3xl" />

      <div className="relative max-w-4xl mx-auto px-4 py-16 sm:py-24">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-4 hover:text-cyan-300">
            ← snap-chart.ai
          </Link>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-purple-400 mb-3">
            ▸ Founding Members
          </div>
          <h1 className="text-4xl sm:text-6xl font-black text-white leading-tight mb-4">
            Lock in <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">early-user pricing</span>.
            <br />Forever.
          </h1>
          <p className="text-lg text-gray-400 max-w-xl mx-auto">
            You&apos;re one of the first traders to use Snap Chart AI. Founding Members lock in a permanent discount — price never goes up.
          </p>
        </div>

        {loading && (
          <div className="text-center py-20">
            <FiLoader className="w-6 h-6 animate-spin text-cyan-400 mx-auto" />
          </div>
        )}

        {!loading && !batch && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border border-white/10 bg-black/40 p-8 text-center"
          >
            <FiUsers className="w-10 h-10 text-gray-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">All founding spots claimed.</h2>
            <p className="text-gray-500 mb-6">
              The founding batch has closed. Standard pricing is still available and you can start with a free plan.
            </p>
            <button
              onClick={() => navigate('/account/billing')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold shadow-lg hover:scale-[1.02] transition-transform"
            >
              See standard plans <FiArrowRight />
            </button>
          </motion.div>
        )}

        {!loading && batch && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl border-2 border-cyan-400/40 bg-gradient-to-br from-cyan-500/10 via-gray-900/70 to-purple-500/10 p-8 sm:p-10 shadow-2xl shadow-cyan-500/20"
          >
            {/* Discount hero */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-400/40 bg-cyan-500/10 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                <span className="font-mono text-[10px] uppercase tracking-widest text-cyan-300">
                  {remaining} / {total} spots left
                </span>
              </div>
              <div className="text-6xl sm:text-8xl font-black bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent leading-none">
                {batch.discount_pct}% OFF
              </div>
              <div className="text-lg text-gray-300 mt-2 font-mono uppercase tracking-widest">
                for life
              </div>
            </div>

            {/* Progress bar */}
            <div className="max-w-md mx-auto mb-6">
              <div className="h-2 rounded-full bg-white/5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${pctFilled}%` }}
                  transition={{ duration: 0.8 }}
                  className={`h-full ${
                    pctFilled >= 90 ? 'bg-orange-400' : 'bg-gradient-to-r from-cyan-400 to-purple-400'
                  }`}
                />
              </div>
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-2">
                <span>{total - remaining} claimed</span>
                <span>{remaining} left</span>
              </div>
            </div>

            {/* Countdown */}
            {countdown && (
              <div className="flex items-center justify-center gap-3 mb-8">
                <FiClock className="w-4 h-4 text-orange-400" />
                <div className="flex items-center gap-1.5 font-mono text-white">
                  {countdown.d > 0 && (
                    <>
                      <span className="text-lg font-bold">{countdown.d}</span>
                      <span className="text-xs text-gray-500 uppercase">d</span>
                    </>
                  )}
                  <span className="text-lg font-bold">{String(countdown.h).padStart(2, '0')}</span>
                  <span className="text-xs text-gray-500 uppercase">h</span>
                  <span className="text-lg font-bold">{String(countdown.m).padStart(2, '0')}</span>
                  <span className="text-xs text-gray-500 uppercase">m</span>
                  <span className="text-lg font-bold">{String(countdown.s).padStart(2, '0')}</span>
                  <span className="text-xs text-gray-500 uppercase">s</span>
                </div>
              </div>
            )}

            {/* Benefits */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {[
                { icon: FiCheck, label: 'Price never goes up' },
                { icon: FiZap, label: 'Full Pro Trader access' },
                { icon: FiShield, label: 'Your discipline score stays' },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2 p-3 rounded-xl border border-white/10 bg-black/40">
                  <b.icon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                  <span className="text-sm text-gray-200">{b.label}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="text-center">
              {error && (
                <div className="mb-4 px-4 py-2.5 rounded-lg border border-red-500/30 bg-red-500/5 text-red-300 font-mono text-xs">
                  ✗ {error}
                </div>
              )}
              <button
                onClick={claim}
                disabled={claiming}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-base font-black shadow-2xl shadow-cyan-500/30 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {claiming ? (
                  <><FiLoader className="w-4 h-4 animate-spin" /> Claiming…</>
                ) : user ? (
                  <>Claim my founding spot <FiArrowRight /></>
                ) : (
                  <><FiLock className="w-4 h-4" /> Sign in to claim</>
                )}
              </button>
              <p className="mt-4 text-[10px] font-mono uppercase tracking-widest text-gray-500">
                30-day money-back · Cancel anytime · Card & crypto
              </p>
            </div>
          </motion.div>
        )}

        {/* Trust footer */}
        <div className="mt-10 text-center text-xs text-gray-600 font-mono">
          Snap Chart AI · The AI that tells you when NOT to trade
        </div>
      </div>
    </div>
  );
};

export default FoundingMember;
